import { FC, useState } from 'react';
import { Button, Checkbox, Col, Divider, Modal, Row } from 'antd';
import { TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragDropContext, Draggable, DragHandle, Droppable, DropResult } from '@/shared/components/drag-and-drop';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import { preferenceModalBodyStyle } from '@/shared/components/modals/preferenceModalStyles';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

import {
    EntityDetailResourceTypeOption,
    EntityDetailTabCustomizationItem,
    getEntityDetailTabPreferences,
    orderEntityDetailTabItems,
} from './preferences';

interface EntityDetailTabsCustomizationModalProps {
    initialResourceType: string;
    onClose: () => void;
    open: boolean;
    preferences: unknown;
    resourceTypes: EntityDetailResourceTypeOption[];
    setPreference: (path: string, value: unknown) => void;
    tabsByResourceType: Record<string, EntityDetailTabCustomizationItem[]>;
}

const draggableIdFor = (resourceType: string, key: string) => `entity-detail:${resourceType}:${key}`;

const EntityDetailTabsCustomizationModal: FC<EntityDetailTabsCustomizationModalProps> = ({
    initialResourceType,
    onClose,
    open,
    preferences,
    resourceTypes,
    setPreference,
    tabsByResourceType,
}) => {
    const { token } = useAntDesignTheme();
    const [resourceType, setResourceType] = useState(initialResourceType);
    const resourceOption = resourceTypes.find((option) => option.value === resourceType);
    const items = tabsByResourceType[resourceType] ?? [];
    const tabPreferences = getEntityDetailTabPreferences(preferences, resourceType);
    const hidden = new Set(tabPreferences.hidden ?? []);
    const orderedItems = orderEntityDetailTabItems(items, tabPreferences);
    const preferencePath = `entityDetail.tabs.${resourceType}`;

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        const nextOrder = orderedItems.map((item) => item.key);
        const [movedKey] = nextOrder.splice(result.source.index, 1);

        nextOrder.splice(result.destination.index, 0, movedKey);
        setPreference(`${preferencePath}.order`, nextOrder);
    };

    const handleVisibilityToggle = (item: EntityDetailTabCustomizationItem) => {
        if (item.locked) {
            return;
        }

        const nextHidden = new Set(hidden);

        if (nextHidden.has(item.key)) {
            nextHidden.delete(item.key);
        } else {
            nextHidden.add(item.key);
        }

        setPreference(`${preferencePath}.hidden`, Array.from(nextHidden));
    };

    return (
        <Modal
            destroyOnClose
            footer={
                <Row>
                    <Col flex='none'>
                        <LearnMoreFooter align='left' topic='company and fund detail tab customization' />
                    </Col>
                    <Col flex='auto' />
                </Row>
            }
            onCancel={onClose}
            open={open}
            styles={{ body: preferenceModalBodyStyle }}
            title={`Customize ${String(resourceOption?.label ?? 'Detail View')}`}
            width={16 * 34}
        >
            <div style={{ marginBottom: '0.75rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Resource Type</small>
                <DropdownButtonSelect<string>
                    matchTriggerWidth
                    menuMinWidth='20rem'
                    onChange={(value) => value && setResourceType(value)}
                    options={resourceTypes.map((option) => ({
                        avatar: { icon: option.icon, props: { size: 24 } },
                        description: option.description,
                        disabled: option.disabled,
                        icon: option.icon,
                        label: option.label,
                        value: option.value,
                    }))}
                    style={{ marginTop: '0.25rem' }}
                    value={resourceType}
                />
            </div>
            <Divider style={{ margin: '0.75rem 0' }} />
            <Row align='middle' gutter={[8, 0]} style={{ marginBottom: '0.25rem' }} wrap={false}>
                <Col flex='auto'>
                    <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>
                        {resourceOption?.label ?? 'Detail'} Tabs
                    </small>
                </Col>
                <Col flex='none'>
                    <SmallTooltip title={`Reset ${String(resourceOption?.label ?? 'Detail')} Tabs`} placement='left'>
                        <Button
                            aria-label={`Reset ${String(resourceOption?.label ?? 'Detail')} Tabs`}
                            onClick={() =>
                                setPreference(preferencePath, {
                                    hidden: [],
                                    order: items.map((item) => item.key),
                                })
                            }
                            size='small'
                            type='text'
                        >
                            <TbRefresh style={{ color: token.colorTextTertiary }} />
                        </Button>
                    </SmallTooltip>
                </Col>
            </Row>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`entity-detail-tabs:${resourceType}`}>
                    {(provided) => (
                        <OverflowFadeScrollArea
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            containerStyle={{
                                backgroundColor: token.colorBgBase,
                                border: `1px dashed ${token.colorBorder}`,
                                borderRadius: token.borderRadius,
                                maxHeight: 'min(60vh, 40rem)',
                            }}
                            style={{ padding: '0.25rem' }}
                            surface='base'
                        >
                            {orderedItems.map((item, index) => (
                                <Draggable
                                    draggableId={draggableIdFor(resourceType, item.key)}
                                    index={index}
                                    key={item.key}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={{
                                                backgroundColor: snapshot.isDragging
                                                    ? token.colorBgElevated
                                                    : token.colorBgBase,
                                                borderRadius: token.borderRadiusSM,
                                                margin: snapshot.isDragging ? 0 : '0.25rem 0',
                                                padding: '0.375rem 0.5rem 0.375rem 0.25rem',
                                                ...provided.draggableProps.style,
                                            }}
                                        >
                                            <DropdownOptionContent
                                                avatar={{ icon: item.icon, props: { size: 24 } }}
                                                control={
                                                    <Row align='middle' gutter={[8, 0]} wrap={false}>
                                                        <Col flex='none'>
                                                            <DragHandle
                                                                dragHandleProps={provided.dragHandleProps}
                                                                isDragging={snapshot.isDragging}
                                                                label={`Reorder ${String(item.label)}`}
                                                            />
                                                        </Col>
                                                        <Col flex='none'>
                                                            <SmallTooltip
                                                                placement='left'
                                                                title={item.locked ? 'Required' : 'Show In Detail View'}
                                                            >
                                                                <Checkbox
                                                                    checked={item.locked || !hidden.has(item.key)}
                                                                    disabled={item.locked}
                                                                    onChange={() => handleVisibilityToggle(item)}
                                                                />
                                                            </SmallTooltip>
                                                        </Col>
                                                    </Row>
                                                }
                                                description={item.description}
                                                title={item.label}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </OverflowFadeScrollArea>
                    )}
                </Droppable>
            </DragDropContext>
            <Divider style={{ margin: '0.75rem 0' }} />
            <Button
                block
                onClick={() =>
                    setPreference(preferencePath, {
                        hidden: [],
                        order: items.map((item) => item.key),
                    })
                }
                type='dashed'
            >
                <TbRefresh />
                Reset {resourceOption?.label ?? 'Detail'} Tabs
            </Button>
        </Modal>
    );
};

export default EntityDetailTabsCustomizationModal;
